import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import { Stack, SvgIcon } from '@mui/material';
import { capitalize } from 'lodash';
import { useState } from 'react';

import { useCustomerInfo } from '@/api/customers';
import { PaymentSessionType } from '@/api/payments';
import { PaymentMethod } from '@/api/subscriptions';
import { AlertChip } from '@/components/AlertChip';
import { PaymentMethodDialog } from '@/components/subscriptions/PaymentMethodDialog';
import { PaymentTroubleAlert } from '@/components/subscriptions/PaymentTroubleAlert';
import { SummaryCard, SummaryCardBody, SummaryCardButton, SummaryCardProps } from '@/components/ui/SummaryCard';
import { errorMessage } from '@/utils';

export type PaymentMethodCardProps = {
    type?: PaymentSessionType;
    onChangePaymentMethod?: (paymentMethod: PaymentMethod) => void;
    immediatePaymentDue?: boolean;
};

export const PaymentMethodCard = ({
    type = 'subscription',
    onChangePaymentMethod,
    immediatePaymentDue,
    ...props
}: PaymentMethodCardProps & Pick<SummaryCardProps, 'sx'>) => {
    const { data: customerInfo, isError, error, isPending: isCustomerInfoPending } = useCustomerInfo();
    const [open, setOpen] = useState(false);
    const defaultPaymentMethod = customerInfo?.data?.default_payment_method;
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();
    const selectedPaymentMethod = paymentMethod ?? defaultPaymentMethod;

    return (
        <SummaryCard
            title={type === 'subscription' ? 'Default payment method' : 'Payment method'}
            {...props}
            isLoading={isCustomerInfoPending}
        >
            <SummaryCardBody>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                    {isError ? (
                        <AlertChip severity="error" label={errorMessage(error, 'Unable to load payment method')} />
                    ) : selectedPaymentMethod ? (
                        <PaymentMethodSummary paymentMethod={selectedPaymentMethod} />
                    ) : (
                        <>None</>
                    )}
                    {!paymentMethod && <PaymentTroubleAlert variant="chip" immediatePaymentDue={immediatePaymentDue} />}
                </Stack>
            </SummaryCardBody>
            <SummaryCardButton onClick={() => setOpen(true)}>Update</SummaryCardButton>
            <PaymentMethodDialog
                open={open}
                type={type}
                onChangePaymentMethod={(paymentMethod) => {
                    if (type === 'payment') {
                        // otherwise, rely on the customer default via queryCache so we stay accurate
                        setPaymentMethod(paymentMethod);
                    }
                    onChangePaymentMethod?.(paymentMethod);
                }}
                onClose={() => setOpen(false)}
            />
        </SummaryCard>
    );
};

const paymentMethodIcon: Record<string, typeof SvgIcon> = {
    card: CreditCardIcon,
    us_bank_account: AccountBalanceIcon,
};

const PaymentMethodSummary = ({ paymentMethod: { type, name, last4 } }: { paymentMethod: PaymentMethod }) => {
    const Icon = paymentMethodIcon[type] ?? PaymentsIcon;
    return (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Icon sx={{ fontSize: '1.3rem' }} />
            {capitalize(name ?? '')} <span>•••• {last4}</span>
        </Stack>
    );
};
